// noinspection JSUnusedGlobalSymbols

import type {EnhancedStore} from "@reduxjs/toolkit";
import {BehaviorSubject} from "rxjs";
import PouchDB from 'pouchdb';
import _ from 'lodash';

import {storeState} from '../models/store.models';
import {StoreConstants, storeExclude} from '../constants/store.constants';

const camelize = (str: string, splitter: string = '_'): string => {
  return _.split(str.toString().replace(/['\u2019]/g, ''), splitter).reduce((result: any, word: any, index: any) => {
    word = word.toLowerCase();
    return result + (index ? _.upperFirst(word) : word);
  }, '');
}

let that: StoreHandler;

export class StoreHandler {
  store: EnhancedStore | undefined;
  state: any;

  [key: string]: any;

  App: any = {
    values(): any {
      return _.get(this.state, `${StoreConstants.StoreGroups.App}`);
    }
  };

  Db: any = {
    values(): any {
      return _.get(this.state, `${StoreConstants.StoreGroups.Db}`);
    }
  };

  Data: any = {
    values(): any {
      return _.get(this.state, `${StoreConstants.StoreGroups.Data}`);
    }
  };

  Device: any = {
    values(): any {
      return _.get(this.state, `${StoreConstants.StoreGroups.Device}`);
    }
  };

  User: any = {
    values(): any {
      return _.get(this.state, `${StoreConstants.StoreGroups.User}`);
    }
  };

  constructor(store: EnhancedStore) {
    that = this;
    this.store = store;
    this.state = store.getState();
    this.initStore();
  }

  public restoreInstances(state: any = this.state) {
    const groups = _.filter(
      _.keys(StoreConstants),
      (f: any) => _.indexOf(storeExclude, f) < 0
    );

    _.each(groups, (group: string) => {
      const groupKeys = _.keys(_.get(StoreConstants, group));
      _.each(groupKeys, (groupKey: string) => {
        const path = `${group}.${camelize(groupKey)}`;
        const original = _.get(storeState, path);
        const current = _.get(state, path);

        if (groupKey.slice(-2) === '$$') {
          _.set(state, path, original);
        }

        if (
          original instanceof BehaviorSubject ||
          original instanceof PouchDB
        ) {
          // Only reassign if the rehydrated value is no longer an instance
          if (
            !(current instanceof BehaviorSubject) &&
            !(current instanceof PouchDB)
          ) {
            if ((original instanceof BehaviorSubject)) (original as BehaviorSubject<any>).next(_.get(current, '_value', null));
            _.set(state, path, original);
          }
        }
      });
    });
    this.state = state;
  }

  public initStore(state: any = this.state) {
    this.state = state;
    const groups = _.filter(
      _.keys(StoreConstants),
      (f: any) => _.indexOf(storeExclude, f) < 0
    );

    _.each(groups, (group: string) => {
      const groupKeys = _.keys(_.get(StoreConstants, group));

      _.each(groupKeys, (groupKey: string) => {
        const path = `${group}.${camelize(groupKey)}`;
        if (_.has(storeState, path)) {
          Object.defineProperty(this[group], camelize(groupKey), {
            get() {
              return _.get(that.state, path);
            },

            set(newValue) {
              let o: any = {
                group: group,
                type: _.get(StoreConstants, `${group}.${groupKey}`),
              };
              _.set(o, `${camelize(groupKey)}`, newValue);
              that?.store?.dispatch(o);
              that.state = that?.store?.getState();
            },

            enumerable: true,
            configurable: true
          });
        }
      });
    });
  }

  public subscribeToRehydrate(persistor: any) {
    persistor.subscribe(() => {
      const {bootstrapped} = persistor.getState();
      if (bootstrapped) {
        this.restoreInstances(this.store?.getState());
      }
    });
  }

  getAction(option: any, group: StoreConstants.StoreGroups): any {
    let _action = {
      type: _.get(StoreConstants, `${group}.${_.toUpper(option)}`),
    };
    _.set(_action, `${option}`, _.get(this.state, `${group}.${option}`));
    return _action;
  }

  dispatch(action: any) {
    this.store?.dispatch(action);
    this.state = this.store?.getState();
  }
}
